import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Search,
    X,
    ArrowUpRight,
    AlertTriangle,
    Shield,
    BookOpen,
} from "lucide-react";
import SEO from "@/components/common/SEO";
import ShareMenu from "@/components/common/ShareMenu";
import { ANTIPOESIA_WARNING } from "@/data/staticContent";
import { api } from "@/lib/api";

const WARNING_STORAGE_KEY = "ad_antipoesia_warning_accepted_v1";

function TriggerWarning({ onAccept, onExit }) {
    return (
        <div
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 overflow-y-auto"
            data-testid="trigger-warning"
            role="dialog"
            aria-modal="true"
            aria-labelledby="trigger-warning-title"
        >
            <div className="grain absolute inset-0 pointer-events-none" />
            <div className="relative max-w-2xl w-full">
                {/* Hazard top */}
                <div className="hazard-stripes h-3 w-full" />

                <div className="bg-[#0d0d0d] border-x-2 border-b-2 border-[var(--warning)] p-8 md:p-12 relative">
                    <div className="flex items-start gap-4 mb-6">
                        <AlertTriangle className="w-10 h-10 text-[var(--warning)] flex-shrink-0 mt-1" />
                        <div>
                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--warning)] mb-2">
                                Advertencia · Lectura responsable
                            </p>
                            <h2
                                id="trigger-warning-title"
                                className="font-display text-3xl md:text-4xl text-[var(--paper-2)] leading-tight"
                            >
                                {ANTIPOESIA_WARNING.title}
                            </h2>
                        </div>
                    </div>

                    <p className="font-mono text-sm md:text-base text-[var(--paper)] leading-relaxed mb-6">
                        {ANTIPOESIA_WARNING.body}
                    </p>

                    <blockquote className="border-l-2 border-[var(--danger)] pl-4 my-8">
                        <p className="font-display italic text-lg md:text-xl text-[var(--paper-2)]">
                            {ANTIPOESIA_WARNING.quote}
                        </p>
                    </blockquote>

                    <ul className="font-mono text-xs text-[var(--ink-muted)] space-y-2 mb-8 border-t border-dashed border-[var(--ink-dim)] pt-6">
                        <li className="flex gap-2">
                            <span className="text-[var(--danger)]">✕</span>
                            Si estás en una recaída activa o crisis, no abras
                            este archivo.
                        </li>
                        <li className="flex gap-2">
                            <span className="text-[var(--danger)]">✕</span>
                            Si tienes pensamientos de hacerte daño, busca ayuda
                            profesional inmediata.
                        </li>
                        <li className="flex gap-2">
                            <span className="text-[var(--danger)]">✕</span>
                            Este archivo no reemplaza terapia. Es testimonio.
                        </li>
                    </ul>

                    <div className="flex flex-col sm:flex-row gap-3">
                        <button
                            onClick={onAccept}
                            className="btn-brutal flex-1 justify-center"
                            data-testid="trigger-warning-accept"
                        >
                            <BookOpen className="w-4 h-4" />
                            {ANTIPOESIA_WARNING.continueLabel}
                        </button>
                        <button
                            onClick={onExit}
                            className="btn-brutal btn-brutal-danger flex-1 justify-center"
                            data-testid="trigger-warning-exit"
                        >
                            <Shield className="w-4 h-4" />
                            {ANTIPOESIA_WARNING.exitLabel}
                        </button>
                    </div>
                </div>

                <div className="hazard-stripes h-3 w-full" />
            </div>
        </div>
    );
}

export default function Antipoesia() {
    const [accepted, setAccepted] = useState(false);
    const [query, setQuery] = useState("");
    const [activeSection, setActiveSection] = useState(null);
    const [fragments, setFragments] = useState([]);
    const [sections, setSections] = useState([]);
    const [loadingData, setLoadingData] = useState(true);

    useEffect(() => {
        try {
            if (sessionStorage.getItem(WARNING_STORAGE_KEY) === "1") {
                setAccepted(true);
            }
        } catch {
            setAccepted(true);
        }
    }, []);

    useEffect(() => {
        Promise.all([
            api.get("/antipoesia/sections"),
            api.get("/antipoesia?limit=1000"),
        ])
            .then(([secRes, fragRes]) => {
                setSections(secRes.data);
                setFragments(fragRes.data);
            })
            .catch(() => {
                setSections([]);
                setFragments([]);
            })
            .finally(() => setLoadingData(false));
    }, []);

    function acceptWarning() {
        try {
            sessionStorage.setItem(WARNING_STORAGE_KEY, "1");
        } catch {}
        setAccepted(true);
    }

    function exitWarning() {
        window.location.href = "/salida";
    }

    // Group fragments by section using fetched data
    const grouped = useMemo(() => {
        const q = query.trim().toLowerCase();
        return sections
            .map((sec) => {
                const items = fragments
                    .map((f, idx) => ({ ...f, idx }))
                    .filter((f) => f.section === sec.id)
                    .filter((f) => {
                        if (!q) return true;
                        return (
                            f.title.toLowerCase().includes(q) ||
                            f.text.toLowerCase().includes(q)
                        );
                    });
                return { ...sec, fragments: items };
            })
            .filter((sec) => sec.fragments.length > 0);
    }, [query, fragments, sections]);

    const totalCount = grouped.reduce(
        (acc, sec) => acc + sec.fragments.length,
        0,
    );

    if (!accepted) {
        return (
            <>
                <SEO
                    title="Antipoesía Terapéutica · Mensajes iniciáticos"
                    description="Más de 130 fragmentos sobre adicción, vacío y recuperación. Advertencia previa de contenido sensible."
                    path="/antipoesia"
                />
                <TriggerWarning onAccept={acceptWarning} onExit={exitWarning} />
            </>
        );
    }

    return (
        <>
            <SEO
                title="Antipoesía Terapéutica · Mensajes iniciáticos"
                description="Dicen que la poesía embellece la vida. La antipoesía la desnuda. Más de 130 fragmentos crudos sobre adicción, vacío, recaída, compañía rota, rehabilitación y recuperación. Por Nelson González (@nitanadicto)."
                path="/antipoesia"
                keywords="antipoesía, terapia, adicción, recuperación, mensajes iniciáticos, NiTanAdicto, Nelson González, Chile"
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "CreativeWorkSeries",
                    name: "Antipoesía Terapéutica · Mensajes iniciáticos",
                    author: { "@type": "Person", name: "Nelson González" },
                    description:
                        "Más de 130 fragmentos breves de antipoesía sobre adicción organizada en 8 secciones temáticas.",
                    numberOfWorks: fragments.length,
                }}
            />

            {/* HERO */}
            <section className="grain relative py-20 md:py-28 border-b border-[var(--line)]">
                <div className="container-x">
                    <div className="flex items-center gap-3 mb-6 flex-wrap">
                        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--danger)]">
                            Versos sin máscara · 07
                        </span>
                        <span className="dotted-div flex-1 min-w-[60px]" />
                        <button
                            onClick={() => {
                                try {
                                    sessionStorage.removeItem(
                                        WARNING_STORAGE_KEY,
                                    );
                                } catch {}
                                setAccepted(false);
                            }}
                            className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-muted)] hover:text-[var(--warning)]"
                            data-testid="show-warning-again"
                        >
                            <AlertTriangle className="w-3 h-3 inline mr-1" />
                            Ver advertencia
                        </button>
                    </div>

                    <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] xl:text-[7rem] leading-[0.85] tracking-tight mb-8">
                        Antipoesía{" "}
                        <span className="text-[var(--danger)]">
                            Terapéutica
                        </span>
                    </h1>
                    <p className="font-mono text-base md:text-lg text-[var(--paper)] max-w-2xl leading-relaxed mb-3">
                        Dicen que la poesía embellece la vida. La antipoesía la
                        desnuda. Aquí las palabras no buscan likes: buscan
                        mostrar la herida sin maquillaje.
                    </p>
                    <p className="font-hand text-2xl text-[var(--warning)] -rotate-1 inline-block">
                        Si te refleja, te duele o te hace espejo — bienvenido a
                        la terapia.
                    </p>

                    {/* Stats + Search */}
                    <div className="mt-12 grid sm:grid-cols-2 gap-6 items-end">
                        <div className="flex gap-8">
                            <div>
                                <p className="font-display text-5xl md:text-6xl text-[var(--danger)] leading-none">
                                    {fragments.length}
                                </p>
                                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-muted)] mt-2">
                                    Fragmentos archivados
                                </p>
                            </div>
                            <div>
                                <p className="font-display text-5xl md:text-6xl text-[var(--warning)] leading-none">
                                    {sections.length}
                                </p>
                                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-muted)] mt-2">
                                    Secciones temáticas
                                </p>
                            </div>
                        </div>
                        <div className="relative">
                            <Search className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--ink-dim)]" />
                            <input
                                type="search"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Buscar en los fragmentos…"
                                className="input-dossier pl-7 pr-8"
                                data-testid="antipoesia-search"
                            />
                            {query && (
                                <button
                                    onClick={() => setQuery("")}
                                    className="absolute right-0 top-1/2 -translate-y-1/2 text-[var(--ink-muted)] hover:text-[var(--danger)]"
                                    aria-label="Limpiar búsqueda"
                                    data-testid="antipoesia-search-clear"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Section quick nav */}
            <nav
                className="sticky top-[88px] z-30 bg-[#0d0d0d]/95 backdrop-blur border-b border-[var(--line)]"
                aria-label="Índice de secciones"
                data-testid="section-nav"
            >
                <div className="container-x py-3 overflow-x-auto">
                    <ul className="flex gap-1 min-w-max">
                        <li>
                            <button
                                onClick={() => setActiveSection(null)}
                                className={`font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-2 border ${!activeSection ? "bg-[var(--paper-2)] text-black border-[var(--paper-2)]" : "border-[var(--line)] text-[var(--paper)] hover:border-[var(--danger)]"}`}
                                data-testid="section-nav-all"
                            >
                                Todo · {totalCount}
                            </button>
                        </li>
                        {grouped.map((sec) => (
                            <li key={sec.id}>
                                <a
                                    href={`#section-${sec.id}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setActiveSection(sec.id);
                                        document
                                            .getElementById(`section-${sec.id}`)
                                            ?.scrollIntoView({
                                                behavior: "smooth",
                                                block: "start",
                                            });
                                    }}
                                    className={`font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-2 border block ${activeSection === sec.id ? "bg-[var(--danger)] text-[var(--paper-2)] border-[var(--danger)]" : "border-[var(--line)] text-[var(--paper)] hover:border-[var(--danger)]"}`}
                                    data-testid={`section-nav-${sec.id}`}
                                >
                                    <span className="text-[var(--ink-dim)] mr-1">
                                        {sec.number}/
                                    </span>
                                    {sec.title} · {sec.fragments.length}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            </nav>

            {/* Sections */}
            {grouped.length === 0 && (
                <section className="py-32 text-center">
                    <p className="font-mono text-sm text-[var(--ink-muted)] mb-4">
                        Sin coincidencias para "{query}".
                    </p>
                    <button
                        onClick={() => setQuery("")}
                        className="btn-brutal btn-brutal-ghost"
                    >
                        Limpiar búsqueda
                    </button>
                </section>
            )}

            {grouped.map((sec) => (
                <section
                    key={sec.id}
                    id={`section-${sec.id}`}
                    className="py-16 md:py-24 border-b border-[var(--line)] scroll-mt-32"
                    data-testid={`section-${sec.id}`}
                >
                    <div className="container-x">
                        {/* Section header */}
                        <header className="mb-12 md:mb-16 grid lg:grid-cols-12 gap-8 items-end">
                            <div className="lg:col-span-2">
                                <p className="font-display text-7xl md:text-8xl lg:text-[10rem] leading-[0.8] text-[var(--danger)] opacity-70">
                                    {sec.number}
                                </p>
                            </div>
                            <div className="lg:col-span-7">
                                <p className="eyebrow mb-3">
                                    Sección · {sec.fragments.length} fragmentos
                                </p>
                                <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-[0.95] mb-3 text-[var(--paper-2)]">
                                    {sec.title}
                                </h2>
                                <p className="font-hand text-xl md:text-2xl text-[var(--warning)] -rotate-1 inline-block">
                                    {sec.subtitle}
                                </p>
                            </div>
                            <div className="lg:col-span-3 lg:text-right">
                                <Stamp number={sec.number} />
                            </div>
                        </header>

                        {/* Fragments grid */}
                        <div
                            className={`grid gap-px bg-[var(--line)] border border-[var(--line)] ${sec.id === "apertura" || sec.id === "linkedin" || sec.id === "reconstruccion" ? "md:grid-cols-2" : "md:grid-cols-2 lg:grid-cols-3"}`}
                        >
                            {sec.fragments.map((p, i) => (
                                <article
                                    key={p.id}
                                    className="bg-[#0d0d0d] p-6 md:p-7 hover:bg-[#1a1a1a] transition-colors group relative flex flex-col"
                                    data-testid={`fragment-${p.idx}`}
                                    id={`fragment-${p.idx + 1}`}
                                >
                                    <div className="flex items-start justify-between gap-3 mb-4">
                                        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-dim)]">
                                            {String(p.idx + 1).padStart(3, "0")}
                                        </p>
                                        <div className="opacity-50 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                                            <ShareMenu
                                                title={p.title}
                                                text={p.text}
                                                path={`/antipoesia#fragment-${p.idx + 1}`}
                                                source="Antipoesía Terapéutica"
                                                testid={`share-frag-${p.idx}`}
                                                titlePosition="end"
                                                hashtags={[
                                                    "nitanadicto",
                                                    "antipoesia",
                                                    "antiterapia",
                                                ]}
                                            />
                                        </div>
                                    </div>
                                    <pre className="font-display italic text-base md:text-lg text-[var(--paper)] whitespace-pre-wrap leading-snug flex-1">
                                        {p.text}
                                    </pre>
                                    <h3 className="font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--warning)] mt-5 pt-4 border-t border-dashed border-[var(--ink-dim)] group-hover:text-[var(--danger)] transition-colors">
                                        — {p.title} —
                                    </h3>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>
            ))}

            {/* Outro CTA */}
            <section className="py-16 md:py-20 bg-[#0a0a0a]">
                <div className="container-x max-w-3xl text-center">
                    <AlertTriangle className="w-10 h-10 text-[var(--danger)] mx-auto mb-6" />
                    <p className="font-display text-3xl md:text-4xl mb-6 leading-tight">
                        Si esto te incomodó, te reflejó o te abrió algo…
                    </p>
                    <p className="font-mono text-sm text-[var(--paper)] leading-relaxed mb-8 max-w-xl mx-auto">
                        No tienes que cargarlo solo. Estos fragmentos son
                        evidencia, no manual. La salida existe y es real.
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                        <Link
                            to="/salida"
                            className="btn-brutal btn-brutal-danger"
                            data-testid="antipoesia-salida-cta"
                        >
                            Salida de emergencia
                            <ArrowUpRight className="w-4 h-4" />
                        </Link>
                        <Link
                            to="/glosario"
                            className="btn-brutal btn-brutal-ghost"
                            data-testid="antipoesia-glosario-cta"
                        >
                            Ir al Glosario
                            <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>
        </>
    );
}

function Stamp({ number }) {
    return (
        <span
            className="stamp stamp-md inline-block"
            style={{ transform: "rotate(6deg)" }}
        >
            Cap. {number}
        </span>
    );
}
